import { OmniaModuleId } from "../generated/enums"
import { AccessPermission, ReadWritePermissions, UserPermissions } from "../permissions/types"
import { omniaResolve } from "../promises"
import { hasAccessPermission } from "../session/utils"
import { IOmniaShell } from "../shell"
import { IOmniaCommand, IOmniaSession, StringLike } from "../types"
import { isObject, isString, isUndefined } from "../utils/utils"
import { Component } from "./component"
import { ComponentContext } from "./componentContext"
import { GlobalFilter, IComponentContext, IComponentDefinition, IComponentStartOptions, IModule } from "./types"

export class Module implements IModule {

    protected session: IOmniaSession

    private _registry: { [key: string]: IComponentDefinition } = {}

    private _init: boolean

    private _components: ComponentContext[] = []
    private _componentsWidth: number

    private _moduleIcon: string
    private _moduleName: StringLike
    private _moduleId: OmniaModuleId
    protected _filter: GlobalFilter

    protected _commands: IOmniaCommand[]



    constructor(protected $scope: ng.IScope, protected shell: IOmniaShell, moduleIcon: string, moduleName: StringLike, moduleId: OmniaModuleId) {

        this.$scope.$on("$destroy", () => this.dispose())

        this.session = shell.getSession()

        this._moduleIcon = moduleIcon
        this._moduleName = moduleName
        this._moduleId = moduleId

        shell.setCurrentModule(this)

        let initWatch = this.$scope.$watch("vm.ready", () => {

            if ((!this._init) && this.ready) {

                this._init = true
                initWatch()

            }

        })

    }

    get ready(): boolean {
        return false
    }

    get initialized(): boolean {
        return this._init
    }

    protected dispose() {
    }

    get moduleIcon() {
        return this._moduleIcon
    }

    get moduleName() {
        return this._moduleName
    }

    get moduleId() {
        return this._moduleId
    }

    get enableAutoRefresh() {
        return !this.getCurrentContext()?.modal
    }

    get conflict() {
        const cmp = this.getCurrentComponent()
        return cmp?.conflict
    }

    get globalFilter() {
        return this._filter
    }

    getCurrentCommands(): IOmniaCommand[] {

        let context = this.getCurrentComponent()

        if (context != null) {
            return context.commands
        }

        return this._commands

    }

    registerComponent(key: string, component: IComponentDefinition) {

        component.directive = component.directive || key

        this._registry[key] = component.reactComponent
            ? {
                ...component,
                // Using a predefined directive for React components
                directive: "omnia-react-component"
            }
            : component

    }

    getComponentDefinition(key: string | IComponentDefinition): IComponentDefinition {

        if (isString(key)) {
            return this._registry[<string>key]
        }

        if (isObject(key)) {
            var def = <IComponentDefinition>key
            if (def.directive) {
                return def
            }
        }

    }

    get components(): IComponentContext[] {
        return this._components
    }

    get componentsWidth(): number {
        return this._componentsWidth
    }

    set componentsWidth(value: number) {
        this._componentsWidth = value
    }

    startComponent(options: IComponentStartOptions, closeCurrent: boolean = false): IComponentContext {

        const modal = this.isComponentModal(options, (this._components.length > 0))

        const component = new ComponentContext(options, this, modal)

        if (closeCurrent && (this._components.length > 0)) {
            this._components = this._components.slice(0, this._components.length - 1).concat([component])
        } else {
            this._components = this._components.concat([component])
        }

        this.updateMenu()

        return component

    }

    closeComponent(ctxt: IComponentContext) {

        const index = this._components.indexOf(<any>ctxt)

        if (index != (this._components.length - 1)) {
            throw Error("")
        }

        this._components = this._components.slice(0, this._components.length - 1)

        this.updateMenu()

    }

    protected startFirstComponent(component: string, prepare: () => ng.IPromise<any>) {

        if (this._components.length) {
            this.closeComponent(this.getCurrentContext())
        }

        this.shell.startBlockUI()

        return prepare().then(r => {

            this.shell.stopBlockUI()

            return this.startComponent({ component })

        })

    }

    private isComponentModal(options: IComponentStartOptions, defaultValue: boolean) {

        let modal: boolean

        if (options) {

            modal = options.blocking

            if (isUndefined(modal)) {
                const def = this.getComponentDefinition(options.component)
                if (def) modal = def.blocking
            }

        }

        if (isUndefined(modal)) {
            modal = defaultValue
        }

        return modal

    }

    protected startFunction(run: () => void, prepare: () => ng.IPromise<any>) {

        this.shell.startBlockUI()

        return prepare().then(r => {

            this.shell.stopBlockUI()

            run()

        })

    }

    public defer(): ng.IDeferred<any> {
        return this.shell.defer()
    }

    public isActive(ctxt: ComponentContext): boolean {
        const i = this._components.indexOf(ctxt)
        return (i == this._components.length - 1)
    }

    public canClose(ctxt: ComponentContext): boolean {
        const i = this._components.indexOf(ctxt)
        return (i && (i == this._components.length - 1))
    }

    public getCurrentContext() {

        let l: number

        if ((l = this._components.length) > 0) {
            return this._components[l - 1]
        }
    }

    getCurrentComponent(): Component {
        return this.getCurrentContext()?.component
    }

    getCurrentFilter() {
        const cmp = this.getCurrentComponent()
        return (cmp ? cmp.globalFilter : null) || this._filter
    }

    getCurrentActivity() {
        return this.getCurrentComponent()?.activity
    }

    private updateMenu() {
        const cmp = this.getCurrentContext()
        this.shell.disableMenu = !!cmp?.modal
    }

    isPinnable(): boolean {

        var ctxt = this.getCurrentContext()

        if (ctxt && ctxt.component) {
            return ctxt.component.pinnable
        }

    }

    getCurrentTitle() {
        return this.getCurrentComponent()?.title ?? this.moduleName
    }

    getCurrentIcon(): string {
        return this.getCurrentComponent()?.icon ?? this.moduleIcon
    }

    canRefresh() {
        return !this.getCurrentContext()?.modal
    }

    refresh(auto: boolean): ng.IPromise<any> {
        // Default implementation
        this.shell.reloadCurrentModule()
        return omniaResolve(null)
    }

    protected checkPermission(selector: (p: UserPermissions) => boolean | AccessPermission | ReadWritePermissions) {

        const perm = this.session.permissions

        if (!perm) {
            return true
        }

        if (hasAccessPermission(selector(perm))) {
            return true
        }

        this.shell.navigate("/")

        return false

    }
}
